import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";

// css
import styles from "./addForm.module.scss";
import { Formik } from "formik";
import AppInputField from "components/shared/FormComponents/AppInputField";
import AppSelectField from "components/shared/FormComponents/AppSelectField";
import { cloneDeep } from "lodash";
import { Link, useNavigate, useParams } from "react-router-dom";
import { successMessageToast } from "helpers/common";
import { MANAGE_COORDINATORS_LIST } from "config/paths";
import LoadingIndicator from "components/shared/LoadingIndicator";
import {addCoordinatorSchema, editCoordinatorSchema} from "./addCoordinator.schema";
import { GRADE_OPTIONS, STATUS_OPTIONS } from "config/constants";
import Switch from "react-switch";
import {
  CommonSelect,
  RequiredFieldAstrick,
} from "components/Common/Common";
import { useSchoolDivisonLookupQuery } from "store/services/schoolsDivisons";
import { useCreateCoordinatorMutation, useGetCoordinatorDetailQuery, useUpdateCoordinatorMutation } from "store/services/coordinators";

const AddCoordinator = () => {
  const { id: coordinatorId } = useParams();
  const editMode = !!coordinatorId;
  const navigate = useNavigate();

  const [initialValues, setInitialValues] = useState({
    firstname: "",
    lastname: "",
    phone: "",
    email: "",
    city: "",
    password: "",
    status: "active",
    grade: "",
    isLoginAllowed: "active",
    school_division: null,
    school_ids: [],
    selected_school: null,
  });

  const { data: schoolDivisionsList, isLoading: schoolDivisionsListLoading } = useSchoolDivisonLookupQuery();

  const [updateCoordinator, { isLoading: updateCoordinatorLoading }] = useUpdateCoordinatorMutation();
  const [createCoordinator, { isLoading: createCoordinatorLoading }] = useCreateCoordinatorMutation();
  const { data: coordinatorDetails, isLoading } = coordinatorId ? useGetCoordinatorDetailQuery(coordinatorId) : {};

  const handleSubmit = async (payload) => {
    try {
      const values = cloneDeep(payload);

      delete values.school_division;
      delete values.selected_school;
      values.school_ids = values.school_ids?.map((item) => item.value);
      values.grade = values.grade?.value;

      const result = editMode
        ? await updateCoordinator({ data: values, coordinatorId })
        : await createCoordinator(values);

      if (result?.error) {
        console.error("Error create coordinator", result.error);
        return;
      }

      successMessageToast(result?.data?.message);

      navigate(MANAGE_COORDINATORS_LIST);
    } catch (err) {
      console.error("Error create coordinator", err);
    }
  };

  const handleChange = (value) => {
    setInitialValues({
      ...initialValues,
      isLoginAllowed: value ? "active" : "archived",
    });
  };

  useEffect(() => {
    if (!coordinatorId) return;

    if (coordinatorDetails) {
      let selectedSchoolDivision =
        schoolDivisionsList?.length &&
        schoolDivisionsList?.find((division) => {
          return (
            division.id ===
            coordinatorDetails?.guardianDetail?.guardianSchool?.division_id
          );
        });

      if (selectedSchoolDivision) {
        selectedSchoolDivision = {
          value: selectedSchoolDivision?.id,
          label: selectedSchoolDivision?.title,
        };
      }

      // multiple schools mapping
      const selectedSchools =
        coordinatorDetails?.guardianSchoolMappings?.map((item) => ({
          value: item?.school_id,
          label: item?.guardianSchoolProfile?.name,
        })) || [];

      setInitialValues({
        firstname: coordinatorDetails?.firstname || "",
        lastname: coordinatorDetails?.lastname || "",
        phone: coordinatorDetails?.phone || "",
        email: coordinatorDetails?.email || "",
        city: coordinatorDetails?.guardianDetail?.city || "",
        status: coordinatorDetails?.status || "",
        grade: coordinatorDetails?.guardianDetail?.grade
          ? {
            value: coordinatorDetails?.guardianDetail?.grade,
            label: coordinatorDetails?.guardianDetail?.grade,
          }
          : "",
        isLoginAllowed: coordinatorDetails?.isLoginAllowed || "active",

        school_division: selectedSchoolDivision,
        school_ids: selectedSchools,

        selected_school: null,
      });
    }
  }, [coordinatorDetails, schoolDivisionsList]);

  return (
    <>
      <section
        className={`${styles.addForm} position-relative py-4 addForm`}
      >
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
                <div className="left">
                  <div className="pageHead pb-3 d-flex align-items-center">
                    <Link
                      className="themeBlue fw-sbold back-to-btn"
                      to={MANAGE_COORDINATORS_LIST}
                    >
                      <svg
                        width="20"
                        height="14"
                        viewBox="0 0 20 14"
                        fill="none"
                      >
                        <path
                          d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z"
                          fill="#1C1B1F"
                        />
                      </svg>{" "}
                      Back
                    </Link>

                    <h6 className="m-0 fw-bold themeBlue">
                      {editMode ? "Edit Coordinator" : "Add New Coordinator"}
                    </h6>
                  </div>
                </div>

                <div className="right">
                  <div className="content d-flex align-items-center">
                    <h6 className="m-0 fw-bold themeBlue px-2">
                      Activate Account
                    </h6>

                    <Switch
                      uncheckedIcon={""}
                      checkedIcon={""}
                      onHandleColor={"#A3DF00"}
                      offHandleColor={"#A3DF00"}
                      onColor={"#E6E6E6"}
                      offColor={"#E6E6E6"}
                      height={18}
                      width={40}
                      onChange={handleChange}
                      checked={initialValues.isLoginAllowed === "active"}
                      disabled={
                        createCoordinatorLoading || updateCoordinatorLoading
                      }
                      defaultChecked={true}
                    />
                  </div>
                </div>
              </div>
            </Col>

            {isLoading ? (
              <LoadingIndicator />
            ) : (
              <Col lg="12" className="my-2">
                <Formik
                  initialValues={initialValues}
                  validationSchema={coordinatorId ? editCoordinatorSchema :  addCoordinatorSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    handleSubmit,
                    values,
                    errors,
                    setFieldTouched,
                    touched,
                    setFieldValue,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      {console.log('Formik errors', errors, values)}
                      <div
                        className="py-3"
                        style={{
                          borderTop: "1px solid #41a2a84d",
                        }}
                      >
                        <Row>
                          <Col md="4" sm="6" className="my-2">
                            <label className="form-label themeBlue fw-bold mb-0 d-flex">
                              Last Name
                              <RequiredFieldAstrick />
                            </label>

                            <AppInputField
                              type="text"
                              name="lastname"
                              placeholder="e.g Smith"
                              className={`${styles.formControl} form-control`}
                              maxLength={30}
                            />
                          </Col>

                          <Col md="4" sm="6" className="my-2">
                            <label className="form-label themeBlue fw-bold mb-0 d-flex">
                              First Name
                              <RequiredFieldAstrick />
                            </label>

                            <AppInputField
                              type="text"
                              name="firstname"
                              placeholder="e.g John"
                              className={`${styles.formControl} form-control`}
                              maxLength={30}
                            />
                          </Col>

                          <Col md="4" sm="6" className="my-2">
                            <label className="form-label themeBlue fw-bold mb-0 d-flex">
                              Phone
                              <RequiredFieldAstrick />
                            </label>

                            <AppInputField
                              type="text"
                              name="phone"
                              placeholder="e.g +1 23234545"
                              className={`${styles.formControl} form-control`}
                              maxLength={25}
                            />
                          </Col>

                          <Col md="4" sm="6" className="my-2">
                            <label className="form-label themeBlue fw-bold mb-0 d-flex">
                              Email
                              <RequiredFieldAstrick />
                            </label>

                            <AppInputField
                              type="text"
                              name="email"
                              placeholder="e.g abc@example.com"
                              className={`${styles.formControl} form-control`}
                            />
                          </Col>

                          {
                            !editMode && <Col md="4" sm="6" className="my-2">
                              <label className="form-label themeBlue fw-bold mb-0 d-flex">
                                Password
                                <RequiredFieldAstrick />
                              </label>

                              <AppInputField
                                type="text"
                                name="password"
                                placeholder="Please enter password"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                          }

                          <Col md="4" sm="6" className="my-2">
                            <label className="form-label themeBlue fw-bold mb-0 d-flex">
                              City
                              <RequiredFieldAstrick />
                            </label>

                            <AppInputField
                              type="text"
                              name="city"
                              placeholder="City"
                              className={`${styles.formControl} form-control`}
                              maxLength={30}
                            />
                          </Col>

                          <Col md="4" sm="6" className="my-2">
                            <label className="form-label themeBlue fw-bold mb-0">
                              Status
                            </label>

                            <AppSelectField
                              name={"status"}
                              placeholder={"Please Select"}
                              className={`${styles.formControl} form-control`}
                              options={STATUS_OPTIONS}
                            />
                          </Col>

                          {/* Division */}
                          <Col md="4" sm="6" className="my-2">
                            <label className="form-label themeBlue fw-bold mb-0 d-flex">
                              School Division
                              <RequiredFieldAstrick />
                            </label>

                            <CommonSelect
                              name="school_division"
                              placeholder={"Please Select Division"}
                              option={
                                schoolDivisionsList?.length
                                  ? schoolDivisionsList?.map((item) => ({
                                    value: item.id,
                                    label: item.title,
                                  }))
                                  : []
                              }
                              onChange={() => {
                                // setFieldValue("school_ids", []);
                                setFieldValue(
                                  "selected_school",
                                  null
                                );
                              }}
                              isLoading={
                                schoolDivisionsListLoading
                              }
                            />
                          </Col>

                          {/* Multiple Schools */}
                          <Col md="4" sm="6" className="my-2">
                            <label className="form-label themeBlue fw-bold mb-0 d-flex">
                              School Name
                              <RequiredFieldAstrick />
                            </label>

                            <div className="d-flex gap-2">
                              <div className="flex-grow-1">
                                <CommonSelect
                                  name="selected_school"
                                  placeholder="Please Select School"
                                  option={(() => {
                                    const selectedDivision =
                                      schoolDivisionsList?.find(
                                        (division) =>
                                          division.id ===
                                          values.school_division
                                            ?.value
                                      );

                                    return selectedDivision
                                      ?.schools?.length
                                      ? selectedDivision.schools
                                        .map(
                                          ({ id, name }) => ({
                                            value: id,
                                            label: name,
                                          })
                                        )
                                        .sort((a, b) =>
                                          a.label.localeCompare(
                                            b.label
                                          )
                                        )
                                      : [];
                                  })()}
                                />
                                {(touched?.school_ids && errors?.school_ids && values?.school_ids?.length == 0) ? (
                                  <div className="error">{errors?.school_ids}</div>
                                ) : null}
                              </div>

                              <Button
                                type="button"
                                className="commonBtn"
                                onClick={() => {
                                  if (
                                    !values.selected_school
                                  )
                                    return;

                                  const alreadyExists =
                                    values.school_ids?.some(
                                      (item) =>
                                        item.value ===
                                        values.selected_school
                                          .value
                                    );

                                  if (alreadyExists)
                                    return;

                                  setFieldValue(
                                    "school_ids",
                                    [
                                      ...(values.school_ids ||
                                        []),
                                      values.selected_school,
                                    ]
                                  );

                                  setFieldValue(
                                    "selected_school",
                                    null
                                  );
                                }}
                              >
                                Add
                              </Button>
                            </div>

                            {/* Bucket List */}
                            <div className="d-flex flex-wrap gap-2 mt-3">
                              {values.school_ids?.map(
                                (school, index) => (
                                  <div
                                    key={school.value}
                                    className="d-flex align-items-center px-3 py-2 rounded-pill"
                                    style={{
                                      background:
                                        "#EEF7F8",
                                      border:
                                        "1px solid #41A2A84D",
                                      fontSize: "14px",
                                    }}
                                  >
                                    <span>
                                      {school.label}
                                    </span>

                                    <button
                                      type="button"
                                      className="ms-2 border-0 bg-transparent"
                                      onClick={() => {
                                        const updatedSchools =
                                          values.school_ids.filter(
                                            (_, i) =>
                                              i !== index
                                          );

                                        setFieldValue(
                                          "school_ids",
                                          updatedSchools
                                        );
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                )
                              )}
                            </div>
                          </Col>

                          {/* Grade */}
                          <Col md="4" sm="6" className="my-2">
                            <label className="form-label themeBlue fw-bold mb-0 d-flex">
                              Grade
                            </label>

                            <CommonSelect
                              name="grade"
                              placeholder="Please Select Grade"
                              option={GRADE_OPTIONS}
                            />
                          </Col>
                        </Row>
                      </div>

                      <Row className="mt-4">
                        <Col className="my-2" lg="12">
                          <div
                            className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                            style={{
                              borderTop:
                                "1px solid #41a2a84d",
                            }}
                          >
                            <Button
                              disabled={
                                createCoordinatorLoading ||
                                updateCoordinatorLoading
                              }
                              type="submit"
                              className="d-flex align-items-center justify-content-center commonBtn"
                            >
                              {createCoordinatorLoading ||
                                updateCoordinatorLoading ? (
                                <LoadingIndicator inline />
                              ) : (
                                "Save"
                              )}
                            </Button>

                            <Button
                              type="button"
                              className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                              onClick={() =>
                                navigate(
                                  MANAGE_COORDINATORS_LIST
                                )
                              }
                            >
                              Cancel
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </Col>
            )}
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddCoordinator;